import { useEffect, useState } from 'react'
import { Box, Flex, Spinner, Text } from '@chakra-ui/react'
import { useParents } from '../hooks/useParents'
import { useParentCoParent } from '../hooks/useParentCoParent'
import ParentList from './ParentList'
import ParentDetails from './ParentDetails'

const DEFAULT_PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 350

export default function ParentRegistryView() {
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    // Store only the id (not the whole parent object) so the selection can't
    // point at stale data once the underlying list is refetched.
    const [selectedId, setSelectedId] = useState<string | null>(null)

    // Debounce the search box before hitting the server, and reset paging /
    // selection so results start from the top.
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput)
            setPage(1)
            setSelectedId(null)
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchInput])

    const { parents, meta, loading, fetching, error } = useParents({
        page,
        limit: pageSize,
        search
    })

    // Keep the selection sticky within the current page; fall back to the
    // first row.
    const selected =
        parents.find((parent) => parent._id === selectedId) ?? parents[0] ?? null

    // The co-parent endpoint now returns the enriched spouse record directly.
    const { coParent } = useParentCoParent(selected?._id)

    if (error) {
        return (
            <Box>
                <Text color="red.500">Error: {error.message}</Text>
            </Box>
        )
    }

    if (loading && parents.length === 0) {
        return (
            <Flex justify="center" align="center" height="400px">
                <Spinner size="xl" />
            </Flex>
        )
    }

    const currentPage = meta?.currentPage ?? page
    const pageCount = meta?.totalPages ?? 1
    const totalCount = meta?.totalCount ?? parents.length

    return (
        <Flex
            gap={4}
            align="stretch"
            direction={{ base: 'column', md: 'row' }}
            flex="1"
            minH="0"
        >
            <Box
                w={{ base: 'full', md: '320px' }}
                flexShrink={0}
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="md"
                overflow="hidden"
            >
                <ParentList
                    parents={parents}
                    total={totalCount}
                    search={searchInput}
                    onSearch={setSearchInput}
                    selectedId={selected?._id ?? null}
                    onSelect={(id) => setSelectedId(id)}
                    page={currentPage}
                    pageCount={pageCount}
                    onPageChange={(nextPage) => {
                        setPage(nextPage)
                        setSelectedId(null)
                    }}
                    pageSize={pageSize}
                    onPageSizeChange={(size) => {
                        setPageSize(size)
                        setPage(1)
                        setSelectedId(null)
                    }}
                    loading={fetching}
                />
            </Box>
            <Box
                flex="1"
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="md"
                overflowY="auto"
            >
                <ParentDetails parent={selected} coParent={coParent} />
            </Box>
        </Flex>
    )
}
